import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { canCreateTestUsers } from '../config/environment';
import './CreateTestUser.css';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:5001'
    : (typeof window !== 'undefined' ? window.location.origin : ''));

const CreateTestUser = ({
  endpoint = '/api/admin/create-test-user',
  defaultCountryCode = '',
}) => {
  const { currentUser } = useAuth();
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    countryCode: defaultCountryCode || currentUser?.country || '',
  });
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/api/auth/countries/active`)
      .then((r) => r.json())
      .then((data) => {
        if (data && data.countries && data.countries.length) {
          setCountries(data.countries);
          setForm((f) =>
            f.countryCode ? f : { ...f, countryCode: data.countries[0].country_code }
          );
        }
      })
      .catch(() => {});
  }, []);

  if (!canCreateTestUsers()) return null;

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(data.user);
        setForm((f) => ({ ...f, username: '', email: '', password: '' }));
      } else {
        setError(data.message || data.error || 'Failed to create test user.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-test-user">
      <h3>🧪 Create Test User</h3>
      <p className="ctu-note">Only available on the test platform.</p>

      {result && (
        <div className="ctu-success">
          ✅ Test user created: <strong>{result.username}</strong> ({result.email})
        </div>
      )}
      {error && <div className="ctu-error">{error}</div>}

      <form onSubmit={handleSubmit} className="ctu-form">
        <input
          name="username"
          type="text"
          placeholder="Username"
          value={form.username}
          onChange={handleChange}
          required
        />
        <input
          name="email"
          type="email"
          placeholder="Email"
          value={form.email}
          onChange={handleChange}
          required
        />
        <input
          name="password"
          type="password"
          placeholder="Password (min 6 characters)"
          value={form.password}
          onChange={handleChange}
          minLength={6}
          required
        />
        <select
          name="countryCode"
          value={form.countryCode}
          onChange={handleChange}
          required
        >
          <option value="">Select country</option>
          {countries.map((c) => (
            <option key={c.country_code} value={c.country_code}>
              {c.country_name} ({c.country_code})
            </option>
          ))}
        </select>
        <button type="submit" disabled={loading}>
          {loading ? 'Creating...' : 'Create Test User'}
        </button>
      </form>
    </div>
  );
};

export default CreateTestUser;
